import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV } from "@/lib/site";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  // Body-Scroll-Lock wenn Mobile-Menü offen
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all ${
        scrolled ? "bg-header/95 backdrop-blur shadow-lg" : "bg-header/70 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 relative flex items-center justify-between h-16 md:h-20 lg:gap-6">
        <Link to="/" aria-label="WM Erneuerbare Energien Startseite" className="flex items-center gap-2 md:gap-3 shrink-0">
          <img
            src="/images/wmee-logo.webp"
            alt="WM Erneuerbare Energien Logo"
            width={200}
            height={80}
            fetchPriority="high"
            decoding="async"
            className="h-10 md:h-12 lg:h-14 w-auto"
          />
          <span className="hidden lg:flex items-center gap-2 font-display">
            <span className="text-3xl xl:text-4xl font-extrabold tracking-tight text-foreground leading-none">WM</span>
            <span className="h-8 xl:h-9 w-px bg-primary/70" />
            <span className="flex flex-col text-xs xl:text-sm uppercase tracking-[0.22em] text-foreground font-semibold leading-tight">
              <span>erneuerbare</span>
              <span>Energien</span>
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10 ml-auto">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <button
                  className="text-xs uppercase tracking-[0.18em] font-semibold transition-colors whitespace-pre-line text-center leading-tight text-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  {item.label}
                  <ChevronDown size={14} />
                </button>
                <div className="absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="bg-header/95 backdrop-blur shadow-lg border border-border rounded-xl py-2 min-w-[200px]">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className={({ isActive }) =>
                          `block px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
                            isActive ? "text-primary" : "text-foreground hover:text-primary"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to!}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.18em] font-semibold transition-colors whitespace-pre-line text-center leading-tight ${
                    isActive ? "text-primary" : "text-foreground hover:text-primary"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        {/* Google-Rating-Badge dezent neben dem Telefon-Icon, nur Desktop */}
        <GoogleRatingBadge size="compact" className="hidden lg:inline-flex shrink-0" />

        <Link
          to="/kontakt"
          aria-label="Persönliches Erstgespräch buchen"
          className="hidden lg:inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground transition-colors shrink-0"
        >
          <Phone size={20} />
        </Link>

        {/* Mobile: Telefon + Hamburger */}
        <div className="lg:hidden flex items-center gap-1">
          <a
            href="tel:+4915150559085"
            aria-label="Anrufen"
            className="inline-flex items-center justify-center w-12 h-12 rounded-full text-primary hover:bg-primary/10 transition-colors"
          >
            <Phone size={22} />
          </a>
          <button
            className="inline-flex items-center justify-center w-12 h-12 rounded-full text-foreground hover:bg-foreground/5 transition-colors"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Slide-In Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      </div>

      <aside
        className={`lg:hidden fixed top-0 right-0 z-50 h-[100dvh] w-[min(88vw,360px)] bg-header shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between h-20 px-5 border-b border-border shrink-0">
          <span className="font-display text-lg font-extrabold tracking-tight text-foreground">Menü</span>
          <button
            onClick={() => setOpen(false)}
            aria-label="Menü schließen"
            className="inline-flex items-center justify-center w-12 h-12 rounded-full text-foreground hover:bg-foreground/5"
          >
            <X size={26} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="border-b border-border/50">
                <button
                  className="w-full flex items-center justify-between px-3 min-h-[52px] text-sm uppercase tracking-[0.18em] font-semibold text-foreground"
                  onClick={() => setOpenGroup((g) => (g === item.label ? null : item.label))}
                  aria-expanded={openGroup === item.label}
                >
                  <span className="text-left">{item.label}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${openGroup === item.label ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-300 ${
                    openGroup === item.label ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="pl-4 pb-2 flex flex-col">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className={({ isActive }) =>
                          `px-3 min-h-[48px] flex items-center text-sm uppercase tracking-[0.18em] font-semibold ${
                            isActive ? "text-primary" : "text-foreground"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to!}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `block px-3 min-h-[52px] flex items-center text-sm uppercase tracking-[0.18em] font-semibold border-b border-border/50 whitespace-normal ${
                    isActive ? "text-primary" : "text-foreground"
                  }`
                }
              >
                {item.label.replace("\n", " ")}
              </NavLink>
            ),
          )}
        </nav>

        <div className="p-4 border-t border-border shrink-0 space-y-2">
          <Link
            to="/kontakt"
            className="flex items-center justify-center gap-2 w-full min-h-[48px] rounded-full bg-primary text-primary-foreground font-semibold uppercase tracking-wide text-sm"
          >
            <Phone size={18} />
            Erstgespräch buchen
          </Link>
        </div>
      </aside>
    </header>
  );
};

export default Header;
